import React, { useState, useEffect } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ThemeProvider, CssBaseline, Box, Tabs, Tab, Paper, IconButton, Tooltip, Typography, Link } from '@mui/material';
import { Calculate, Event, Brightness4, Brightness7 } from '@mui/icons-material';
import UnitConverter from './components/UnitConverter.tsx';
import DateCalculator from './components/DateCalculator.tsx';
import ConsentManager from './components/ConsentManager.tsx';
import AnalyticsDebug from './components/AnalyticsDebug.tsx';
import { 
  trackPageView, 
  trackThemeChange, 
  trackConsentChange, 
  initSessionTracking,
  trackSessionEnd 
} from './services/analytics.ts';
import { getTheme } from './theme.ts';
import './App.css';

function App() {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(() => {
    const stored = localStorage.getItem('analytics-consent');
    return stored === 'true';
  });
  const [activeTab, setActiveTab] = useState(0);
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const stored = localStorage.getItem('colorMode');
    if (stored === 'light' || stored === 'dark') return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  // Replace with your actual Google Analytics Measurement ID
  const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-KC9HY9YNBR';


  useEffect(() => {
    // Track initial page view if analytics is enabled
    if (analyticsEnabled && GA_MEASUREMENT_ID) {
      trackPageView(window.location.pathname);
      initSessionTracking();
    }
  }, [analyticsEnabled, GA_MEASUREMENT_ID]);

  useEffect(() => {
    localStorage.setItem('colorMode', mode);
    
    // Track theme changes if analytics is enabled
    if (analyticsEnabled) {
      trackThemeChange(mode);
    }
  }, [mode, analyticsEnabled]);

  const handleConsentChange = (consent: boolean) => {
    setAnalyticsEnabled(consent);
    
    // Track consent changes
    trackConsentChange(consent);
    
    // Initialize session tracking if consent is given
    if (consent) {
      initSessionTracking();
    }
  };

  // Track session end when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (analyticsEnabled) {
        trackSessionEnd();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [analyticsEnabled]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    console.log('🔍 Tab change detected:', { newValue, analyticsEnabled });
    setActiveTab(newValue);

    // Track tab change as a page view
    if (analyticsEnabled) {
      console.log('🔍 Analytics enabled, tracking tab change...');
      if (newValue === 0) {
        console.log('🔍 Tracking Unit Converter tab');
        trackPageView('/unit-converter', 'Unit Converter');
      } else if (newValue === 1) {
        console.log('🔍 Tracking Date Calculator tab');
        trackPageView('/date-calculator', 'Date Calculator');
      }
    } else {
      console.log('⚠️ Analytics disabled, not tracking tab change');
    }
  };

  const toggleMode = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  return (
    <HelmetProvider>
      <ThemeProvider theme={getTheme(mode)}>
        <CssBaseline />
        
        {/* Dynamic SEO Meta Tags */}
        <Helmet>
          <title>
            {activeTab === 0 ? 'Unit Converter - Free Online Conversion Tools' : 'Date Calculator - Calculate Date Differences'}
          </title>
          <meta name="description" content={
            activeTab === 0 
              ? 'Free online unit converter with 8 categories: Length, Weight, Temperature, Volume, Area, Speed, Time, and Data. Convert between meters, kilograms, Celsius, liters, and more instantly.'
              : 'Free online date calculator to calculate the difference between two dates. Perfect for project planning, age calculation, and time management.'
          } />
          <meta property="og:title" content={
            activeTab === 0 ? 'Unit Converter - Free Online Conversion Tools' : 'Date Calculator - Calculate Date Differences'
          } />
          <meta property="og:description" content={
            activeTab === 0 
              ? 'Free online unit converter with 8 categories: Length, Weight, Temperature, Volume, Area, Speed, Time, and Data. Convert between meters, kilograms, Celsius, liters, and more instantly.'
              : 'Free online date calculator to calculate the difference between two dates. Perfect for project planning, age calculation, and time management.'
          } />
          <meta property="og:url" content={`https://your-domain.com${activeTab === 0 ? '/unit-converter' : '/date-calculator'}`} />
          <link rel="canonical" href={`https://your-domain.com${activeTab === 0 ? '/unit-converter' : '/date-calculator'}`} />
        </Helmet>
        
        <Box 
          sx={{ 
            minHeight: '100vh', 
            background: mode === 'light'
              ? 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
              : 'linear-gradient(135deg, #181a20 0%, #23272f 100%)',
            py: 2,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
        {/* Skip Link for Accessibility */}
        <Box
          component="a"
          href="#main-content"
          sx={{
            position: 'absolute',
            top: '-40px',
            left: '6px',
            zIndex: 9999,
            color: 'white',
            backgroundColor: 'primary.main',
            padding: '8px 16px',
            textDecoration: 'none',
            borderRadius: '4px',
            fontSize: '14px',
            fontWeight: 600,
            '&:focus': {
              top: '6px',
              outline: '2px solid',
              outlineColor: 'white',
              outlineOffset: '2px',
            }
          }}
        >
          Skip to main content
        </Box>
        
        {/* Theme Toggle Button */}
        <Box sx={{ position: 'fixed', top: 24, right: 32, zIndex: 1200 }}>
          <Tooltip title={mode === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}>
            <IconButton 
              onClick={toggleMode} 
              color="inherit" 
              size="large" 
              aria-label={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}
              sx={{ 
                bgcolor: 'background.paper', 
                boxShadow: 2,
                '&:focus': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: '2px',
                }
              }}
            >
              {mode === 'light' ? <Brightness4 /> : <Brightness7 />}
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* Main Content */}
        <Box id="main-content" sx={{ flex: 1 }}>
          {/* Main App Tabs */}
          <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 } }}>
            <Paper 
              elevation={3} 
              sx={{ 
                mb: 3,
                borderRadius: 3,
                overflow: 'hidden',
                border: '1px solid rgba(0,0,0,0.05)'
              }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="fullWidth"
                aria-label="Main application tabs"
                sx={{ 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                  '& .MuiTabs-scrollButtons': {
                    color: 'primary.main',
                  }
                }}
              >
                <Tab 
                  icon={<Calculate sx={{ fontSize: 28 }} />}
                  label="Unit Converter" 
                  iconPosition="top"
                  aria-label="Unit Converter tab"
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    minHeight: 72,
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 700,
                    },
                    '&:focus': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    }
                  }}
                />
                <Tab 
                  icon={<Event sx={{ fontSize: 28 }} />}
                  label="Date Calculator" 
                  iconPosition="top"
                  aria-label="Date Calculator tab"
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    minHeight: 72,
                    '&.Mui-selected': {
                      color: 'primary.main',
                      fontWeight: 700,
                    },
                    '&:focus': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: '2px',
                    }
                  }}
                />
              </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && <UnitConverter />}
            {activeTab === 1 && <DateCalculator />}
          </Box>
        </Box>

        {/* Footer */}
        <Box 
          component="footer"
          sx={{ 
            mt: 'auto', 
            py: 2, 
            textAlign: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            opacity: 0.8
          }}
        >
          <Typography variant="body2" color="text.secondary">
            © 2024 Unit Converter. Made with ❤️ for easy conversions.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            <Link 
              href="/privacy-policy.html" 
              target="_blank" 
              rel="noopener noreferrer"
              color="inherit"
              underline="hover"
              aria-label="Read our privacy policy (opens in new tab)"
              sx={{ fontWeight: 500 }}
            >
              Privacy Policy
            </Link>
          </Typography>
        </Box>

        <ConsentManager 
          measurementId={GA_MEASUREMENT_ID}
          onConsentChange={handleConsentChange}
        />

        <AnalyticsDebug analyticsEnabled={analyticsEnabled} />
      </Box>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
